import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { firestoreService } from '@/services/firestoreService';
import { functionsService } from '@/services/functionsService';
import { Input } from './ui/Input';

interface Company {
  id: string;
  name: string;
  ticker: string;
  sector?: string;
  logo?: string;
}

interface CompanySearchProps {
  onSelect?: (company: Company) => void;
  className?: string;
  placeholder?: string;
}

export const CompanySearch: React.FC<CompanySearchProps> = ({
  onSelect,
  className = '',
  placeholder = 'Search companies by name or ticker...',
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [results, setResults] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchTerm.length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    const searchCompanies = async () => {
      setLoading(true);
      try {
        // Search Firestore first
        const allCompanies = await firestoreService.getDocuments<Company>('companies', [
          firestoreService.orderBy('name', 'asc'),
        ]);

        let filtered = allCompanies
          .filter(
            (company) =>
              company.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              company.ticker.toLowerCase().includes(searchTerm.toLowerCase())
          )
          .slice(0, 5);

        // If not enough results, search FMP API
        if (filtered.length < 5) {
          try {
            const fmpResult = await functionsService.searchCompaniesFMP(searchTerm);
            if (fmpResult.success && fmpResult.results) {
              const fmpCompanies: Company[] = fmpResult.results.map((result: any) => ({
                id: result.symbol,
                name: result.name,
                ticker: result.symbol,
                sector: undefined,
                logo: undefined,
              }));

              // Merge results, avoiding duplicates
              const existingTickers = new Set(filtered.map(c => c.ticker.toUpperCase()));
              const newCompanies = fmpCompanies.filter(
                c => !existingTickers.has(c.ticker.toUpperCase())
              );
              
              filtered = [...filtered, ...newCompanies].slice(0, 8);
            }
          } catch (fmpError) {
            console.error('FMP search failed:', fmpError);
            // Continue with Firestore results only
          }
        }

        setResults(filtered);
        setShowResults(true);
        setSelectedIndex(-1);
      } catch (error) {
        console.error('Failed to search companies:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchCompanies, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchTerm]);

  const handleSelect = (company: Company) => {
    setSearchTerm('');
    setShowResults(false);
    if (onSelect) {
      onSelect(company);
    } else {
      navigate(`/companies/${company.ticker.toUpperCase()}`);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < results.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter' && selectedIndex >= 0 && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    } else if (e.key === 'Escape') {
      setShowResults(false);
    }
  };

  return (
    <div ref={searchRef} className={`relative ${className}`}>
      <div className="relative">
        <Input
          type="text"
          placeholder={placeholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          onFocus={() => searchTerm.length >= 2 && setShowResults(true)}
          onKeyDown={handleKeyDown}
          className="pl-10 pr-10"
        />
        <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blazerex-green"></div>
          </div>
        )}
        {searchTerm && !loading && (
          <button
            onClick={() => {
              setSearchTerm('');
              setShowResults(false);
            }}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            ×
          </button>
        )}
      </div>

      {showResults && results.length > 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
          {results.map((company, index) => (
            <button
              key={company.id}
              onClick={() => handleSelect(company)}
              className={`w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                index === selectedIndex ? 'bg-gray-50' : ''
              } ${index !== results.length - 1 ? 'border-b border-gray-100' : ''}`}
            >
              {company.logo && (
                <img
                  src={company.logo}
                  alt={company.name}
                  className="w-8 h-8 rounded flex-shrink-0"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="body-text font-medium text-gray-900 truncate">
                  {company.name}
                </p>
                <p className="body-text text-sm text-gray-500 font-mono">
                  {company.ticker}
                </p>
              </div>
            </button>
          ))}
        </div>
      )}

      {showResults && searchTerm.length >= 2 && !loading && results.length === 0 && (
        <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
          <p className="body-text text-gray-600 text-center">
            No companies found matching "{searchTerm}"
          </p>
        </div>
      )}
    </div>
  );
};

